import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowRight, MessageSquare, Shield, Zap, Clock, CheckCircle2, Globe } from "lucide-react";
import { SITE_URL } from "@/lib/site-url";
import { apiUrl } from "@/lib/api-base";
import { SERVICE_NAMES } from "@/lib/hero_services";
import { isLegacyCodeUrl, serviceCodeFromParam, serviceSlug } from "@/lib/service-slugs";
import { TopBar } from "@/components/top-bar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const validLocales = ["en", "es", "fr", "de", "it", "pt", "zh", "ru"] as const;
type Locale = (typeof validLocales)[number];

/** Deterministic offset so each service always shows the same related set. */
function hashCode(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) hash = (hash * 31 + value.charCodeAt(i)) | 0;
  return hash;
}

// ─── i18n ──────────────────────────────────────────────────────────────────
const UI: Record<
  Locale,
  {
    // The <title> and <meta description>. They used to be built inline in `head()` from
    // one hardcoded English string, so all eight locales of every service shipped byte
    // identical metadata — ~4000 URLs whose heads differed only in the canonical. That
    // is the shape Google files under "crawled, currently not indexed".
    metaTitle: (name: string) => string;
    metaDesc: (name: string, price: string) => string;
    tagline: (name: string) => string;
    heading: (name: string) => string;
    sub: (name: string) => string;
    howTitle: string;
    step1t: string;
    step1d: string;
    step2t: string;
    step2d: string;
    step3t: string;
    step3d: string;
    from: string;
    available: string;
    cta: (name: string) => string;
    ctaSub: string;
    ctaBtn: string;
    loginBtn: string;
    breadHome: string;
    breadPricing: string;
    featuresTitle: string;
    f1: string;
    f2: string;
    f3: string;
    f4: string;
    faqTitle: string;
    // Both are called with the service name — `q` was typed as a plain string while every
    // locale supplied a function, so the whole table was type-checked against a lie.
    faq: Array<{ q: (name: string) => string; a: (name: string) => string }>;
    relatedTitle: string;
    allServices: string;
  }
> = {
  en: {
    metaTitle: (name) => `${name} Virtual Number for SMS Verification — SmsEmpire`,
    metaDesc: (name, price) =>
      `Get a temporary virtual number to verify ${name} by SMS. From $${price}, pay per code, refunded automatically if it never arrives. 40+ countries, no SIM and no subscription.`,
    tagline: (name) => `SMS Verification for ${name}`,
    heading: (name) => `Buy a ${name} Phone Number Online`,
    sub: (name) =>
      `Get a temporary virtual number for ${name} verification in seconds. Pay only if the code arrives — automatic refund otherwise.`,
    howTitle: "How it works",
    step1t: "Create an account",
    step1d: "Sign up in 30 seconds — no credit card required.",
    step2t: "Get your number",
    step2d: "Select the service, choose a country and receive your virtual number instantly.",
    step3t: "Receive the code",
    step3d: "The SMS code appears in your dashboard within seconds. Copy it and you're done.",
    from: "from",
    available: "numbers in stock",
    cta: (name) => `Get a ${name} number now`,
    ctaSub: "Pay-as-you-go — no subscription needed.",
    ctaBtn: "Get started free",
    loginBtn: "Log in",
    breadHome: "Home",
    breadPricing: "Pricing",
    featuresTitle: "Why choose SmsEmpire?",
    f1: "Only pay for successful SMS codes",
    f2: "Automatic refund if no code arrives",
    f3: "Numbers from 40+ countries",
    f4: "Instant delivery — usually under 30 seconds",
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: (name: string) => `Is it legal to use a virtual number for ${name}?`,
        a: (name: string) =>
          `Yes. Using a temporary number for ${name} verification is legal for personal use, testing, and privacy purposes. SmsEmpire does not support use for fraud or illegal activities.`,
      },
      {
        q: (_: string) => "What happens if the code doesn't arrive?",
        a: (_: string) =>
          "If no SMS code arrives within the waiting period, the activation is automatically cancelled and your balance is fully refunded. You never pay for failed verifications.",
      },
      {
        q: (name: string) => `Can I receive multiple ${name} codes?`,
        a: (name: string) =>
          `Each activation gives you one number. You can purchase additional activations for ${name} as many times as you need — the balance system is fully pay-as-you-go.`,
      },
    ],
    relatedTitle: "Other services you can verify",
    allServices: "See all services",
  },
  es: {
    metaTitle: (name) => `Número virtual para verificar ${name} por SMS — SmsEmpire`,
    metaDesc: (name, price) =>
      `Consigue un número virtual temporal para verificar ${name} por SMS. Desde $${price}, pagas por código y se te reembolsa solo si no llega. Más de 40 países, sin SIM ni suscripción.`,
    tagline: (name) => `Verificación SMS para ${name}`,
    heading: (name) => `Comprar número de teléfono para ${name} online`,
    sub: (name) =>
      `Obtén un número virtual temporal para verificar ${name} en segundos. Paga solo si llega el código — reembolso automático si no.`,
    howTitle: "Cómo funciona",
    step1t: "Crea una cuenta",
    step1d: "Regístrate en 30 segundos — sin tarjeta de crédito.",
    step2t: "Obtén tu número",
    step2d: "Selecciona el servicio, elige un país y recibe tu número virtual al instante.",
    step3t: "Recibe el código",
    step3d: "El código SMS aparece en tu panel en segundos. Cópialo y listo.",
    from: "desde",
    available: "números en stock",
    cta: (name) => `Obtener número para ${name} ahora`,
    ctaSub: "Pago por uso — sin suscripción.",
    ctaBtn: "Empezar gratis",
    loginBtn: "Iniciar sesión",
    breadHome: "Inicio",
    breadPricing: "Precios",
    featuresTitle: "¿Por qué elegir SmsEmpire?",
    f1: "Solo pagas por los códigos SMS exitosos",
    f2: "Reembolso automático si no llega ningún código",
    f3: "Números de más de 40 países",
    f4: "Entrega instantánea — normalmente en menos de 30 segundos",
    faqTitle: "Preguntas frecuentes",
    faq: [
      {
        q: (name: string) => `¿Es legal usar un número virtual para ${name}?`,
        a: (name: string) =>
          `Sí. Usar un número temporal para verificar ${name} es legal para uso personal, pruebas y privacidad. SmsEmpire no apoya el uso para fraude o actividades ilegales.`,
      },
      {
        q: (_: string) => "¿Qué pasa si el código no llega?",
        a: (_: string) =>
          "Si no llega ningún código SMS en el período de espera, la activación se cancela automáticamente y tu saldo se reembolsa completamente. Nunca pagas por verificaciones fallidas.",
      },
      {
        q: (name: string) => `¿Puedo recibir múltiples códigos de ${name}?`,
        a: (name: string) =>
          `Cada activación te da un número. Puedes comprar activaciones adicionales para ${name} tantas veces como necesites — el sistema de saldo es totalmente de pago por uso.`,
      },
    ],
    relatedTitle: "Otros servicios que puedes verificar",
    allServices: "Ver todos los servicios",
  },
  fr: {
    metaTitle: (name) => `Numéro virtuel pour vérifier ${name} par SMS — SmsEmpire`,
    metaDesc: (name, price) =>
      `Obtenez un numéro virtuel temporaire pour vérifier ${name} par SMS. À partir de $${price}, paiement au code et remboursement automatique s\u2019il n\u2019arrive pas. Plus de 40 pays, sans SIM ni abonnement.`,
    tagline: (name) => `Vérification SMS pour ${name}`,
    heading: (name) => `Acheter un numéro de téléphone ${name} en ligne`,
    sub: (name) =>
      `Obtenez un numéro virtuel temporaire pour la vérification ${name} en quelques secondes. Ne payez que si le code arrive — remboursement automatique sinon.`,
    howTitle: "Comment ça marche",
    step1t: "Créez un compte",
    step1d: "Inscrivez-vous en 30 secondes — sans carte de crédit.",
    step2t: "Obtenez votre numéro",
    step2d:
      "Sélectionnez le service, choisissez un pays et recevez votre numéro virtuel instantanément.",
    step3t: "Recevez le code",
    step3d:
      "Le code SMS apparaît dans votre tableau de bord en quelques secondes. Copiez-le et vous avez terminé.",
    from: "à partir de",
    available: "numéros en stock",
    cta: (name) => `Obtenir un numéro ${name} maintenant`,
    ctaSub: "Paiement à l'usage — sans abonnement.",
    ctaBtn: "Commencer gratuitement",
    loginBtn: "Se connecter",
    breadHome: "Accueil",
    breadPricing: "Tarifs",
    featuresTitle: "Pourquoi choisir SmsEmpire ?",
    f1: "Payez uniquement pour les codes SMS réussis",
    f2: "Remboursement automatique si aucun code n'arrive",
    f3: "Numéros de 40+ pays",
    f4: "Livraison instantanée — généralement en moins de 30 secondes",
    faqTitle: "Questions fréquemment posées",
    faq: [
      {
        q: (name: string) => `Est-il légal d'utiliser un numéro virtuel pour ${name} ?`,
        a: (name: string) =>
          `Oui. Utiliser un numéro temporaire pour la vérification ${name} est légal pour usage personnel, tests et confidentialité. SmsEmpire ne soutient pas l'utilisation frauduleuse.`,
      },
      {
        q: (_: string) => "Que se passe-t-il si le code n'arrive pas ?",
        a: (_: string) =>
          "Si aucun code SMS n'arrive dans le délai d'attente, l'activation est automatiquement annulée et votre solde est intégralement remboursé.",
      },
      {
        q: (name: string) => `Puis-je recevoir plusieurs codes ${name} ?`,
        a: (name: string) =>
          `Chaque activation vous donne un numéro. Vous pouvez acheter des activations supplémentaires pour ${name} autant de fois que nécessaire.`,
      },
    ],
    relatedTitle: "Autres services vérifiables",
    allServices: "Voir tous les services",
  },
  de: {
    metaTitle: (name) => `Virtuelle Nummer für die ${name} SMS-Verifizierung — SmsEmpire`,
    metaDesc: (name, price) =>
      `Temporäre virtuelle Nummer für die SMS-Verifizierung von ${name}. Ab $${price}, Bezahlung pro Code und automatische Erstattung, wenn keiner ankommt. Über 40 Länder, ohne SIM und ohne Abo.`,
    tagline: (name) => `SMS-Verifizierung für ${name}`,
    heading: (name) => `${name} Telefonnummer online kaufen`,
    sub: (name) =>
      `Erhalten Sie eine temporäre virtuelle Nummer für die ${name}-Verifizierung in Sekunden. Zahlen Sie nur wenn der Code ankommt — andernfalls automatische Erstattung.`,
    howTitle: "So funktioniert es",
    step1t: "Konto erstellen",
    step1d: "In 30 Sekunden registrieren — keine Kreditkarte erforderlich.",
    step2t: "Nummer erhalten",
    step2d: "Dienst auswählen, Land wählen und virtuelle Nummer sofort erhalten.",
    step3t: "Code empfangen",
    step3d: "Der SMS-Code erscheint in Sekunden in Ihrem Dashboard. Kopieren und fertig.",
    from: "ab",
    available: "Nummern auf Lager",
    cta: (name) => `Jetzt ${name}-Nummer erhalten`,
    ctaSub: "Pay-as-you-go — kein Abonnement erforderlich.",
    ctaBtn: "Kostenlos starten",
    loginBtn: "Anmelden",
    breadHome: "Startseite",
    breadPricing: "Preise",
    featuresTitle: "Warum SmsEmpire wählen?",
    f1: "Zahlen Sie nur für erfolgreiche SMS-Codes",
    f2: "Automatische Erstattung wenn kein Code ankommt",
    f3: "Nummern aus 40+ Ländern",
    f4: "Sofortige Lieferung — normalerweise unter 30 Sekunden",
    faqTitle: "Häufig gestellte Fragen",
    faq: [
      {
        q: (name: string) => `Ist es legal, eine virtuelle Nummer für ${name} zu verwenden?`,
        a: (name: string) =>
          `Ja. Die Verwendung einer temporären Nummer für die ${name}-Verifizierung ist für den persönlichen Gebrauch, Tests und Datenschutz legal.`,
      },
      {
        q: (_: string) => "Was passiert wenn der Code nicht ankommt?",
        a: (_: string) =>
          "Wenn innerhalb der Wartezeit kein SMS-Code ankommt, wird die Aktivierung automatisch storniert und Ihr Guthaben vollständig erstattet.",
      },
      {
        q: (name: string) => `Kann ich mehrere ${name}-Codes erhalten?`,
        a: (name: string) =>
          `Jede Aktivierung gibt Ihnen eine Nummer. Sie können so viele ${name}-Aktivierungen kaufen wie Sie benötigen.`,
      },
    ],
    relatedTitle: "Weitere Dienste, die du verifizieren kannst",
    allServices: "Alle Dienste ansehen",
  },
  it: {
    metaTitle: (name) => `Numero virtuale per verificare ${name} via SMS — SmsEmpire`,
    metaDesc: (name, price) =>
      `Ottieni un numero virtuale temporaneo per verificare ${name} via SMS. Da $${price}, paghi a codice e vieni rimborsato in automatico se non arriva. Oltre 40 paesi, senza SIM e senza abbonamento.`,
    tagline: (name) => `Verifica SMS per ${name}`,
    heading: (name) => `Compra un numero di telefono ${name} online`,
    sub: (name) =>
      `Ottieni un numero virtuale temporaneo per la verifica ${name} in pochi secondi. Paga solo se il codice arriva — rimborso automatico altrimenti.`,
    howTitle: "Come funziona",
    step1t: "Crea un account",
    step1d: "Registrati in 30 secondi — senza carta di credito.",
    step2t: "Ottieni il tuo numero",
    step2d:
      "Seleziona il servizio, scegli un paese e ricevi il tuo numero virtuale istantaneamente.",
    step3t: "Ricevi il codice",
    step3d: "Il codice SMS appare nella tua dashboard in pochi secondi. Copialo e hai finito.",
    from: "da",
    available: "numeri in stock",
    cta: (name) => `Ottieni un numero ${name} ora`,
    ctaSub: "Pay-as-you-go — nessun abbonamento necessario.",
    ctaBtn: "Inizia gratuitamente",
    loginBtn: "Accedi",
    breadHome: "Home",
    breadPricing: "Prezzi",
    featuresTitle: "Perché scegliere SmsEmpire?",
    f1: "Paga solo per i codici SMS riusciti",
    f2: "Rimborso automatico se nessun codice arriva",
    f3: "Numeri da 40+ paesi",
    f4: "Consegna istantanea — di solito in meno di 30 secondi",
    faqTitle: "Domande frequenti",
    faq: [
      {
        q: (name: string) => `È legale usare un numero virtuale per ${name}?`,
        a: (name: string) =>
          `Sì. Usare un numero temporaneo per la verifica ${name} è legale per uso personale, test e privacy.`,
      },
      {
        q: (_: string) => "Cosa succede se il codice non arriva?",
        a: (_: string) =>
          "Se nessun codice SMS arriva entro il periodo di attesa, l'attivazione viene automaticamente annullata e il tuo saldo viene completamente rimborsato.",
      },
      {
        q: (name: string) => `Posso ricevere più codici ${name}?`,
        a: (name: string) =>
          `Ogni attivazione ti dà un numero. Puoi acquistare attivazioni aggiuntive per ${name} quante volte vuoi.`,
      },
    ],
    relatedTitle: "Altri servizi che puoi verificare",
    allServices: "Vedi tutti i servizi",
  },
  pt: {
    metaTitle: (name) => `Número virtual para verificar ${name} por SMS — SmsEmpire`,
    metaDesc: (name, price) =>
      `Obtenha um número virtual temporário para verificar ${name} por SMS. A partir de $${price}, paga por código e é reembolsado automaticamente se não chegar. Mais de 40 países, sem SIM nem subscrição.`,
    tagline: (name) => `Verificação SMS para ${name}`,
    heading: (name) => `Comprar número de telefone ${name} online`,
    sub: (name) =>
      `Obtenha um número virtual temporário para verificação ${name} em segundos. Pague apenas se o código chegar — reembolso automático caso contrário.`,
    howTitle: "Como funciona",
    step1t: "Crie uma conta",
    step1d: "Cadastre-se em 30 segundos — sem cartão de crédito.",
    step2t: "Obtenha seu número",
    step2d: "Selecione o serviço, escolha um país e receba seu número virtual instantaneamente.",
    step3t: "Receba o código",
    step3d: "O código SMS aparece no seu painel em segundos. Copie e pronto.",
    from: "a partir de",
    available: "números em estoque",
    cta: (name) => `Obter número ${name} agora`,
    ctaSub: "Pay-as-you-go — sem necessidade de assinatura.",
    ctaBtn: "Começar gratuitamente",
    loginBtn: "Entrar",
    breadHome: "Início",
    breadPricing: "Preços",
    featuresTitle: "Por que escolher SmsEmpire?",
    f1: "Pague apenas por códigos SMS bem-sucedidos",
    f2: "Reembolso automático se nenhum código chegar",
    f3: "Números de 40+ países",
    f4: "Entrega instantânea — geralmente em menos de 30 segundos",
    faqTitle: "Perguntas frequentes",
    faq: [
      {
        q: (name: string) => `É legal usar um número virtual para ${name}?`,
        a: (name: string) =>
          `Sim. Usar um número temporário para verificação ${name} é legal para uso pessoal, testes e privacidade.`,
      },
      {
        q: (_: string) => "O que acontece se o código não chegar?",
        a: (_: string) =>
          "Se nenhum código SMS chegar dentro do período de espera, a ativação é automaticamente cancelada e seu saldo é totalmente reembolsado.",
      },
      {
        q: (name: string) => `Posso receber vários códigos ${name}?`,
        a: (name: string) =>
          `Cada ativação fornece um número. Você pode comprar ativações adicionais para ${name} quantas vezes precisar.`,
      },
    ],
    relatedTitle: "Outros serviços que pode verificar",
    allServices: "Ver todos os serviços",
  },
  ru: {
    metaTitle: (name) => `Виртуальный номер для верификации ${name} по SMS — SmsEmpire`,
    metaDesc: (name, price) =>
      `Временный виртуальный номер для подтверждения ${name} по SMS. От $${price}, оплата за код и автоматический возврат, если он не пришёл. Более 40 стран, без SIM и без подписки.`,
    tagline: (name) => `SMS-верификация для ${name}`,
    heading: (name) => `Купить номер телефона для ${name} онлайн`,
    sub: (name) =>
      `Получите временный виртуальный номер для верификации ${name} за секунды. Платите только если код пришёл — автоматический возврат средств в противном случае.`,
    howTitle: "Как это работает",
    step1t: "Создайте аккаунт",
    step1d: "Зарегистрируйтесь за 30 секунд — без банковской карты.",
    step2t: "Получите номер",
    step2d: "Выберите сервис, выберите страну и получите виртуальный номер мгновенно.",
    step3t: "Получите код",
    step3d: "SMS-код появится в вашей панели за секунды. Скопируйте его — и готово.",
    from: "от",
    available: "номеров в наличии",
    cta: (name) => `Получить номер для ${name} сейчас`,
    ctaSub: "Оплата по факту — подписка не нужна.",
    ctaBtn: "Начать бесплатно",
    loginBtn: "Войти",
    breadHome: "Главная",
    breadPricing: "Цены",
    featuresTitle: "Почему SmsEmpire?",
    f1: "Платите только за успешные SMS-коды",
    f2: "Автоматический возврат если код не пришёл",
    f3: "Номера из 40+ стран",
    f4: "Мгновенная доставка — обычно менее 30 секунд",
    faqTitle: "Частые вопросы",
    faq: [
      {
        q: (name: string) => `Законно ли использовать виртуальный номер для ${name}?`,
        a: (name: string) =>
          `Да. Использование временного номера для верификации ${name} законно для личного использования, тестирования и защиты конфиденциальности.`,
      },
      {
        q: (_: string) => "Что если код не пришёл?",
        a: (_: string) =>
          "Если SMS-код не приходит в течение периода ожидания, активация автоматически отменяется и баланс полностью возвращается.",
      },
      {
        q: (name: string) => `Можно ли получить несколько кодов для ${name}?`,
        a: (name: string) =>
          `Каждая активация даёт один номер. Вы можете покупать дополнительные активации для ${name} столько раз, сколько нужно.`,
      },
    ],
    relatedTitle: "Другие сервисы для верификации",
    allServices: "Все сервисы",
  },
  zh: {
    metaTitle: (name) => `${name} 短信验证虚拟号码 — SmsEmpire`,
    metaDesc: (name, price) =>
      `获取用于 ${name} 短信验证的临时虚拟号码。$${price} 起，按验证码计费，收不到自动退款。覆盖 40 多个国家，无需 SIM 卡，无需订阅。`,
    tagline: (name) => `${name} SMS验证`,
    heading: (name) => `在线购买${name}手机号码`,
    sub: (name) =>
      `在几秒钟内获得用于${name}验证的临时虚拟号码。仅在收到验证码时付费——否则自动退款。`,
    howTitle: "如何使用",
    step1t: "创建账户",
    step1d: "30秒内注册——无需信用卡。",
    step2t: "获取号码",
    step2d: "选择服务，选择国家，立即获得虚拟号码。",
    step3t: "接收验证码",
    step3d: "SMS验证码在几秒内出现在您的控制台中。复制即可完成。",
    from: "从",
    available: "个号码有货",
    cta: (name) => `立即获取${name}号码`,
    ctaSub: "按需付费——无需订阅。",
    ctaBtn: "免费开始",
    loginBtn: "登录",
    breadHome: "首页",
    breadPricing: "价格",
    featuresTitle: "为什么选择SmsEmpire？",
    f1: "只为成功的SMS验证码付费",
    f2: "如果没有收到验证码，自动退款",
    f3: "来自40多个国家的号码",
    f4: "即时交付——通常在30秒内",
    faqTitle: "常见问题",
    faq: [
      {
        q: (name: string) => `使用虚拟号码进行${name}验证合法吗？`,
        a: (name: string) =>
          `是的。使用临时号码进行${name}验证用于个人使用、测试和隐私保护是合法的。`,
      },
      {
        q: (_: string) => "如果验证码没有到来怎么办？",
        a: (_: string) => "如果在等待期内没有收到SMS验证码，激活将自动取消，您的余额将全额退还。",
      },
      {
        q: (name: string) => `我可以收到多个${name}验证码吗？`,
        a: (name: string) => `每次激活给您一个号码。您可以根据需要购买多次${name}激活。`,
      },
    ],
    relatedTitle: "其他可验证的服务",
    allServices: "查看全部服务",
  },
};

type ServiceData = { code: string; name: string; price_available: number; count: number } | null;

export const Route = createFileRoute("/$lang/sms/$service")({
  beforeLoad: ({ params }) => {
    if (!validLocales.includes(params.lang as Locale)) throw notFound();
    if (!serviceCodeFromParam(params.service)) throw notFound();
    // Old provider-code links keep working, but only one URL is the page: send them to
    // the readable one so the ranking signals do not sit on two addresses.
    if (isLegacyCodeUrl(params.service)) {
      throw redirect({
        to: "/$lang/sms/$service",
        params: { lang: params.lang, service: serviceSlug(params.service) },
        statusCode: 301,
      });
    }
  },
  loader: async ({ params }) => {
    const lang = params.lang as Locale;
    const service = serviceCodeFromParam(params.service) ?? params.service;
    const name = SERVICE_NAMES[service] ?? service;
    let serviceData: ServiceData = null;
    try {
      const res = await fetch(apiUrl("/api/public/services"));
      if (res.ok) {
        const all: ServiceData[] = await res.json();
        serviceData = (all as any[]).find((s: any) => s.code === service) ?? null;
      }
    } catch {
      /* ignore */
    }
    return { lang, service, name, serviceData };
  },
  // The route is created with `as any` (see the bottom of this call), so nothing here is
  // inferred — the annotations are what keep `head` honest about what it reads.
  head: ({
    params,
    loaderData,
  }: {
    params: Record<string, string>;
    loaderData?: { serviceData: ServiceData };
  }) => {
    const lang = (params.lang as Locale) ?? "en";
    const ui = UI[lang] ?? UI.en;
    const service = serviceCodeFromParam(params.service) ?? params.service;
    const slug = serviceSlug(service);
    const name = SERVICE_NAMES[service] ?? service;
    const canonical = `${SITE_URL}/${lang}/sms/${slug}`;
    // The schema price has to be the price on the page. It was pinned at "0.35" — the
    // cheapest service on the catalogue — while the page rendered the real
    // `price_available` right next to it, so Google was told one number and shown
    // another on every service except the cheapest. Falls back to the old constant
    // only when the provider lookup in the loader failed and no price is displayed.
    const livePrice = loaderData?.serviceData?.price_available;
    const schemaPrice =
      typeof livePrice === "number" && livePrice > 0 ? livePrice.toFixed(2) : "0.35";
    // Both come from the locale table, and the price is the live one. The description
    // used to say "from $0.35" — the cheapest service in the catalogue — on every page
    // regardless of what the page itself displayed, which is the same lie the schema
    // price carried until it was fixed above.
    const title = ui.metaTitle(name);
    const desc = ui.metaDesc(name, schemaPrice);

    return {
      meta: [
        { title },
        { name: "description", content: desc },
        {
          name: "keywords",
          content: `${name} virtual number, ${name} sms verification, ${name} phone number, receive ${name} sms online, ${name} verification code, temporary number ${name}`,
        },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        // Overrides the site-wide default so the Product `image` below and the social
        // card are the same asset, which is what Google expects them to be.
        { property: "og:image", content: `${SITE_URL}/og/sms.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: ui.metaTitle(name) },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/sms/${slug}` },
        ...validLocales.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/sms/${slug}`,
        })),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${name} Virtual Phone Number`,
            description: desc,
            url: canonical,
            // Required by Google, and its absence was the one *critical* error in
            // Search Console. There is no per-service artwork for 500 services, so this
            // is the product-level card — a real, on-brand 1200x630 image rather than a
            // placeholder, and the same one the og:image above points at.
            image: [`${SITE_URL}/og/sms.png`],
            brand: { "@type": "Brand", name: "SmsEmpire" },
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              price: schemaPrice,
              // Prices track the upstream provider and are re-quoted at purchase, so a
              // 30-day validity is a claim we can actually keep.
              validFrom: new Date().toISOString().split("T")[0],
              priceValidUntil: new Date(Date.now() + 30 * 24 * 3600 * 1000)
                .toISOString()
                .split("T")[0],
              availability: "https://schema.org/InStock",
              url: canonical,
              seller: { "@type": "Organization", name: "SmsEmpire" },
              // Describes the refund the service actually gives: cancel inside the
              // 20-minute window, or let it expire without a code, and the charge comes
              // back in full. No `shippingDetails` — nothing is shipped, and inventing
              // delivery terms for a virtual product to satisfy a checklist would be a
              // misrepresentation.
              hasMerchantReturnPolicy: {
                "@type": "MerchantReturnPolicy",
                applicableCountry: "US",
                returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
                merchantReturnDays: 1,
                returnMethod: "https://schema.org/ReturnByMail",
                returnFees: "https://schema.org/FreeReturn",
                url: `${SITE_URL}/${lang}/refund`,
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: ui.heading(name),
            description: ui.sub(name),
            totalTime: "PT2M",
            estimatedCost: { "@type": "MonetaryAmount", currency: "USD", value: schemaPrice },
            step: [
              { "@type": "HowToStep", position: 1, name: ui.step1t, text: ui.step1d },
              { "@type": "HowToStep", position: 2, name: ui.step2t, text: ui.step2d },
              { "@type": "HowToStep", position: 3, name: ui.step3t, text: ui.step3d },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: UI[lang].faq.map((f) => ({
              "@type": "Question",
              name: f.q(name),
              acceptedAnswer: { "@type": "Answer", text: f.a(name) },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: ui.breadHome, item: `${SITE_URL}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: ui.breadPricing,
                item: `${SITE_URL}/${lang}/pricing`,
              },
              { "@type": "ListItem", position: 3, name: name, item: canonical },
            ],
          }),
        },
      ],
    };
  },
  component: ServicePage,
} as any);

function ServicePage() {
  const { lang, service, name, serviceData } = Route.useLoaderData() as {
    lang: Locale;
    service: string;
    name: string;
    serviceData: ServiceData;
  };
  const ui = UI[lang] ?? UI.en;
  const { user } = useAuth();

  // A stable neighbourhood per service rather than a random sample: the same page always
  // links to the same siblings, so the link graph a crawler sees does not churn.
  const related = useMemo(() => {
    const all = Object.keys(SERVICE_NAMES)
      .filter((code) => code !== service && SERVICE_NAMES[code].trim())
      .sort();
    if (all.length === 0) return [];
    const start = Math.abs(hashCode(service)) % all.length;
    return Array.from({ length: Math.min(12, all.length) }, (_, i) => {
      const code = all[(start + i) % all.length];
      return { code, name: SERVICE_NAMES[code].trim(), slug: serviceSlug(code) };
    });
  }, [service]);

  const features = [
    { icon: CheckCircle2, text: ui.f1 },
    { icon: Shield, text: ui.f2 },
    { icon: Globe, text: ui.f3 },
    { icon: Clock, text: ui.f4 },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[500px] bg-gradient-hero"
        aria-hidden
      />
      <TopBar />
      <main className="relative mx-auto max-w-4xl px-6 pt-12 pb-24">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">
            {ui.breadHome}
          </Link>
          <span>/</span>
          <Link to="/$lang/pricing" params={{ lang }} className="hover:text-primary">
            {ui.breadPricing}
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{name}</span>
        </nav>

        {/* Hero */}
        <div className="mx-auto max-w-3xl text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
            <MessageSquare className="h-3.5 w-3.5" /> {ui.tagline(name)}
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {ui.heading(name)}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">{ui.sub(name)}</p>

          {/* Price badge */}
          {serviceData && serviceData.price_available > 0 && (
            <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-6 py-4">
              <Zap className="h-5 w-5 text-primary" />
              <div className="text-left">
                <p className="text-xs text-muted-foreground">{ui.from}</p>
                <p className="text-2xl font-bold text-primary">
                  ${serviceData.price_available.toFixed(2)}
                </p>
              </div>
              {serviceData.count > 0 && (
                <div className="ml-4 border-l border-border pl-4 text-left">
                  <p className="text-xs text-muted-foreground">{ui.available}</p>
                  <p className="text-lg font-semibold text-foreground">
                    {serviceData.count.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* CTA buttons */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="bg-gradient-primary shadow-glow">
              <Link
                to={user ? "/dashboard" : "/auth"}
                search={user ? undefined : { tab: "register" }}
              >
                {ui.ctaBtn} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            {!user && (
              <Button asChild size="lg" variant="outline">
                <Link to="/auth" search={{ tab: "login" }}>
                  {ui.loginBtn}
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* How it works */}
        <section aria-labelledby="how-heading" className="mb-16">
          <h2
            id="how-heading"
            className="mb-8 text-center text-2xl font-bold tracking-tight sm:text-3xl"
          >
            {ui.howTitle}
          </h2>
          <ol className="grid gap-6 sm:grid-cols-3">
            {[
              { title: ui.step1t, desc: ui.step1d },
              { title: ui.step2t, desc: ui.step2d },
              { title: ui.step3t, desc: ui.step3d },
            ].map((step, i) => (
              <li
                key={i}
                className="relative rounded-2xl border border-border bg-card p-6 shadow-elegant"
              >
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-primary text-sm font-bold text-primary-foreground shadow-glow">
                  {i + 1}
                </div>
                <h3 className="font-semibold text-foreground">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Features */}
        <section aria-labelledby="features-heading" className="mb-16">
          <h2
            id="features-heading"
            className="mb-8 text-center text-2xl font-bold tracking-tight sm:text-3xl"
          >
            {ui.featuresTitle}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {features.map((f, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5"
              >
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <f.icon className="h-4 w-4" />
                </div>
                <p className="text-sm text-muted-foreground">{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-heading" className="mb-16">
          <h2
            id="faq-heading"
            className="mb-8 text-center text-2xl font-bold tracking-tight sm:text-3xl"
          >
            {ui.faqTitle}
          </h2>
          <div className="space-y-4">
            {ui.faq.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-border bg-card p-5 shadow-elegant open:shadow-glow"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-base font-semibold text-foreground">
                  {f.q(name)}
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-primary transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a(name)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA section */}
        <section aria-labelledby="cta-heading">
          <div className="mx-auto rounded-3xl border border-primary/30 bg-gradient-primary p-10 text-center shadow-glow">
            <h2
              id="cta-heading"
              className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl"
            >
              {ui.cta(name)}
            </h2>
            <p className="mt-3 text-primary-foreground/90">{ui.ctaSub}</p>
            <div className="mt-6">
              <Button asChild size="lg" variant="secondary">
                <Link
                  to={user ? "/dashboard" : "/auth"}
                  search={user ? undefined : { tab: "register" }}
                >
                  {ui.ctaBtn} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Related services. Gives every service page outgoing links, so the catalogue is
            reachable by following links instead of only through the sitemap. */}
        <section aria-labelledby="related-heading" className="mt-16">
          <h2 id="related-heading" className="text-2xl font-bold tracking-tight text-foreground">
            {ui.relatedTitle}
          </h2>
          <ul className="mt-5 flex flex-wrap gap-2">
            {related.map((r) => (
              <li key={r.code}>
                <Link
                  to="/$lang/sms/$service"
                  params={{ lang, service: r.slug }}
                  className="inline-block rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  {r.name}
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/$lang/sms"
            params={{ lang }}
            className="mt-5 inline-block text-sm font-medium text-primary hover:underline"
          >
            {ui.allServices} →
          </Link>
        </section>
      </main>
    </div>
  );
}
